import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { merge, of } from 'rxjs';
import { catchError, debounceTime, filter, map, startWith, switchMap, tap } from 'rxjs/operators';

import { jobCreateCustomerFeature } from 'src/app/job/job-create/job-create-customer/job-create-customer.feature';
import { getCustomerInfo } from 'src/app/job/job-helpers';
import { NewOpportunityCustomerActions } from 'src/app/new-opportunity/new-opportunity-customer/new-opportunity-customer.actions';
import { SIMILAR_JOBS_MAX } from 'src/app/new-opportunity/new-opportunity-similar-jobs/new-opportunity-similar-jobs.component';
import { BrandingService } from 'src/app/services/branding.service';
import { DetailsHelperService } from 'src/app/services/details-helper.service';
import { PermissionService } from 'src/app/services/permission.service';
import { FullUserFragment, ListUsersGQL, SimilarJobsGQL } from 'src/generated/graphql.generated';

import { NewOpportunitySimilarJobsActions } from './new-opportunity-similar-jobs.actions';
import { newOpportunitySimilarJobsFeature } from './new-opportunity-similar-jobs.feature';

export const searchForSimilarJobs = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    searchJobsGQL = inject(SimilarJobsGQL),
    branding = inject(BrandingService),
    permissionHandler = inject(PermissionService),
) => {

    const inputChanges$ = actions$.pipe(
        ofType(
          NewOpportunityCustomerActions.fullNameInputChanged,
          NewOpportunityCustomerActions.emailInputChanged,
          NewOpportunityCustomerActions.phoneInputChanged,
          NewOpportunityCustomerActions.companyInputChanged,
          NewOpportunityCustomerActions.additionalContactFieldChanged,
        ),
        debounceTime(300),
        concatLatestFrom(() => store.select(createSelector(
          jobCreateCustomerFeature.selectCustomerEditor,
          newOpportunitySimilarJobsFeature.selectQuery,
          ({ selectedCustomer }, query ) => ({
            fullName: getCustomerInfo(selectedCustomer).fullName,
            query,
          }),
        ))),
        filter(([ _action, { query, fullName }]) => query !== fullName),
        map(([ _action, { query }]) => query.trim()),
    );

    const customerSelected$ = actions$.pipe(
        ofType(NewOpportunityCustomerActions.existingCustomerSelected),
        filter(({ existingCustomer }) => Boolean(existingCustomer?.id)),
        map(({ existingCustomer }) => {
            const name = [existingCustomer?.givenName, existingCustomer?.familyName]
                .filter(Boolean).join(' ');
            return name || existingCustomer?.email || existingCustomer?.phone || '';
        }),
    );

    return merge(inputChanges$, customerSelected$).pipe(
        filter((search) => search.length > 0),
        concatLatestFrom(() => permissionHandler.watchPermission('search.searchJobs')),
        switchMap(([ search, hasSearchPermission ]) => {
            if (!hasSearchPermission) {
                return of(NewOpportunitySimilarJobsActions.similarJobsPermissionDenied());
            }
            return searchJobsGQL.fetch(
              { search, limit: SIMILAR_JOBS_MAX + 1, zone: branding.currentZone().value.id },
              { fetchPolicy: 'network-only' },
            ).pipe(
                map((res) => NewOpportunitySimilarJobsActions.searchSimilarJobsSuccess({
                    jobs: res.data?.searchJobs?.results ?? [],
                })),
                catchError((error) => of(NewOpportunitySimilarJobsActions.searchSimilarJobsError({ error }))),
                startWith(NewOpportunitySimilarJobsActions.searchSimilarJobsStarted()),
            );
        }),
    );
}, { functional: true, dispatch: true });

export const openJobDetails = createEffect((
    actions$ = inject(Actions),
    detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
        ofType(NewOpportunitySimilarJobsActions.jobRowClicked),
        tap(({ jobId }) => detailsHelper.open('job', { id: jobId })),
    );
}, { functional: true, dispatch: false });

export const confirmDiscardChanges = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(
          NewOpportunitySimilarJobsActions.continueJobButtonClicked,
          NewOpportunitySimilarJobsActions.openCustomerButtonClicked,
        ),
        map((action) => NewOpportunitySimilarJobsActions.openSimilarJobStarted({
            jobId: action.jobId,
            zoneId: action.zoneId,
        })),
    );
}, { functional: true, dispatch: true });

// Select Job: open the existing job in the side panel
export const openSimilarJobDirectly = createEffect((
    actions$ = inject(Actions),
    detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
        ofType(NewOpportunitySimilarJobsActions.openSimilarJobDirectly),
        tap(({ jobId }) => detailsHelper.open('job', { id: jobId })),
    );
}, { functional: true, dispatch: false });

export const openSimilarJob = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    listUsersGQL = inject(ListUsersGQL),
) => {
    return actions$.pipe(
        ofType(
          NewOpportunitySimilarJobsActions.openSimilarJobStarted,
        ),
        concatLatestFrom(({ jobId }) => store.select(newOpportunitySimilarJobsFeature.selectJobs).pipe(
            map((jobs) => jobs.find((j) => j.id === jobId)),
        )),
        filter(([ _action, job ]) => Boolean(job)),
        map(([ { jobId }, job ]) => ({
            jobId,
            customerId: job!.customerId ?? undefined,
        })),
        switchMap(({ customerId }) => {
            if (!customerId) {
                return of(NewOpportunitySimilarJobsActions.openSimilarJobError({
                    error: new Error('Selected job has no customer user'),
                }));
            }

            // Fetch the full customer user (the search result only returns
            // customerId; the form needs FullUserFragment).
            return listUsersGQL.fetch({
                filter: { userIds: [ customerId ] },
                limit: 1,
            }, { fetchPolicy: 'network-only' }).pipe(
                map((res) => {
                    const fullUser = (res.data?.usersv2?.users ?? [])[0] as FullUserFragment | undefined;
                    return NewOpportunityCustomerActions.existingCustomerSelected({
                        existingCustomer: fullUser ?? ({ id: customerId } as FullUserFragment),
                    });
                }),
                catchError((error) => of(NewOpportunitySimilarJobsActions.openSimilarJobError({
                    error,
                }))),
            );
        }),
    );
}, { functional: true, dispatch: true });
